import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ScrollProgress } from "@/components/scroll-progress";
import { CookieConsent } from "@/components/cookie-consent";
import { AnalyticsRouteTracker } from "@/components/analytics-route-tracker";
import { lazy, Suspense } from "react";

// Eager: landing route to keep first paint fast.
import Index from "./pages/Index";

// Lazy-loaded routes — keep initial bundle small.
const Services = lazy(() => import("./pages/Services"));
const Portfolio = lazy(() => import("./pages/Portfolio"));
const PortfolioCase = lazy(() => import("./pages/PortfolioCase"));
const About = lazy(() => import("./pages/About"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Contact = lazy(() => import("./pages/Contact"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ThankYou = lazy(() => import("./pages/ThankYou"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPostSalesProspecting = lazy(() => import("./pages/BlogPostSalesProspecting"));
const BlogPostSpreadsheetHiddenCosts = lazy(() => import("./pages/BlogPostSpreadsheetHiddenCosts"));
const BlogPostCustomToolsSalesTeamsLoveCrm = lazy(() => import("./pages/BlogPostCustomToolsSalesTeamsLoveCrm"));
const BlogPostSmallAutomationWins = lazy(() => import("./pages/BlogPostSmallAutomationWins"));
const BlogPostSimpleClientPortal = lazy(() => import("./pages/BlogPostSimpleClientPortal"));
const BlogPostCustomAppProjectCriteria = lazy(() => import("./pages/BlogPostCustomAppProjectCriteria"));
const BlogPostHowToBriefADeveloper = lazy(() => import("./pages/BlogPostHowToBriefADeveloper"));
const BlogPostWhyAiToolsFailInCompanies = lazy(() => import("./pages/BlogPostWhyAiToolsFailInCompanies"));
const BlogPostFirstWeekInternalAppRollout = lazy(() => import("./pages/BlogPostFirstWeekInternalAppRollout"));
const BlogPostKeepPromisesToClients = lazy(() => import("./pages/BlogPostKeepPromisesToClients"));
const BlogPostQuietPowerSmallInternalTools = lazy(() => import("./pages/BlogPostQuietPowerSmallInternalTools"));
const BlogPostProcessesDeserveApp = lazy(() => import("./pages/BlogPostProcessesDeserveApp"));
const BlogPostCustomAppVsSaas = lazy(() => import("./pages/BlogPostCustomAppVsSaas"));
const BlogPostCustomVsGenericProspecting = lazy(() => import("./pages/BlogPostCustomVsGenericProspecting"));
const BlogPostWhyTeamsIgnoreNewTools = lazy(() => import("./pages/BlogPostWhyTeamsIgnoreNewTools"));
const BlogPostProspectingLessDraining = lazy(() => import("./pages/BlogPostProspectingLessDraining"));
const BlogPostFromIdeaToAppInWeeks = lazy(() => import("./pages/BlogPostFromIdeaToAppInWeeks"));
const BlogPostCustomToolsPeaceOfMind = lazy(() => import("./pages/BlogPostCustomToolsPeaceOfMind"));
const BlogPostUsefulVsPrettyDashboards = lazy(() => import("./pages/BlogPostUsefulVsPrettyDashboards"));
const BlogPostAfterLaunchAppHealthy = lazy(() => import("./pages/BlogPostAfterLaunchAppHealthy"));
const BlogPostHowToTalkAboutToolsWithTeam = lazy(() => import("./pages/BlogPostHowToTalkAboutToolsWithTeam"));
const BlogPostSmallToolsBigImpact = lazy(() => import("./pages/BlogPostSmallToolsBigImpact"));
const BlogPostWorkflowTooMessy = lazy(() => import("./pages/BlogPostWorkflowTooMessy"));
const BlogPostWhatSalesTeamsNeedProspecting = lazy(() => import("./pages/BlogPostWhatSalesTeamsNeedProspecting"));
const BlogPostInternalToolsFailTooBroad = lazy(() => import("./pages/BlogPostInternalToolsFailTooBroad"));
const BlogPostReduceAdminWithoutChaos = lazy(() => import("./pages/BlogPostReduceAdminWithoutChaos"));
const BlogPostClientFacingToolCustomerSide = lazy(() => import("./pages/BlogPostClientFacingToolCustomerSide"));
const BlogPostSoftwareShouldRemoveDecisions = lazy(() => import("./pages/BlogPostSoftwareShouldRemoveDecisions"));
const BlogPostSalesToDeliveryHandover = lazy(() => import("./pages/BlogPostSalesToDeliveryHandover"));
const BlogPostTooManyToolsWhatToFixFirst = lazy(() => import("./pages/BlogPostTooManyToolsWhatToFixFirst"));
const BlogPostAutomateQualification = lazy(() => import("./pages/BlogPostAutomateQualification"));
const BlogPostInternalToolsStartWithFrustration = lazy(() => import("./pages/BlogPostInternalToolsStartWithFrustration"));
const BlogPostWebAppVsMobileApp = lazy(() => import("./pages/BlogPostWebAppVsMobileApp"));

const queryClient = new QueryClient();

const RouteFallback = () => (
  <div className="min-h-screen bg-[#0a0a0a]" aria-hidden="true" />
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <ScrollProgress />
      <BrowserRouter>
        <AnalyticsRouteTracker />
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/services" element={<Services />} />
            <Route path="/portfolio" element={<Portfolio />} />
            <Route path="/portfolio/:slug" element={<PortfolioCase />} />
            <Route path="/about" element={<About />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/thank-you" element={<ThankYou />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/sales-prospecting-app" element={<BlogPostSalesProspecting />} />
            <Route path="/blog/spreadsheet-hidden-costs" element={<BlogPostSpreadsheetHiddenCosts />} />
            <Route path="/blog/custom-tools-sales-teams-love-crm" element={<BlogPostCustomToolsSalesTeamsLoveCrm />} />
            <Route path="/blog/small-automation-wins" element={<BlogPostSmallAutomationWins />} />
            <Route path="/blog/simple-client-portal" element={<BlogPostSimpleClientPortal />} />
            <Route path="/blog/custom-app-project-criteria" element={<BlogPostCustomAppProjectCriteria />} />
            <Route path="/blog/how-to-brief-a-developer" element={<BlogPostHowToBriefADeveloper />} />
            <Route path="/blog/why-ai-tools-fail-in-companies" element={<BlogPostWhyAiToolsFailInCompanies />} />
            <Route path="/blog/first-week-internal-app-rollout" element={<BlogPostFirstWeekInternalAppRollout />} />
            <Route path="/blog/keep-promises-to-clients" element={<BlogPostKeepPromisesToClients />} />
            <Route path="/blog/quiet-power-small-internal-tools" element={<BlogPostQuietPowerSmallInternalTools />} />
            <Route path="/blog/processes-that-deserve-their-own-app" element={<BlogPostProcessesDeserveApp />} />
            <Route path="/blog/custom-app-vs-generic-saas" element={<BlogPostCustomAppVsSaas />} />
            <Route path="/blog/custom-vs-generic-prospecting-tools" element={<BlogPostCustomVsGenericProspecting />} />
            <Route path="/blog/why-teams-ignore-new-tools" element={<BlogPostWhyTeamsIgnoreNewTools />} />
            <Route path="/blog/prospecting-less-draining" element={<BlogPostProspectingLessDraining />} />
            <Route path="/blog/from-idea-to-app-in-weeks" element={<BlogPostFromIdeaToAppInWeeks />} />
            <Route path="/blog/custom-tools-peace-of-mind" element={<BlogPostCustomToolsPeaceOfMind />} />
            <Route path="/blog/useful-vs-pretty-dashboards" element={<BlogPostUsefulVsPrettyDashboards />} />
            <Route path="/blog/after-launch-keeping-app-healthy" element={<BlogPostAfterLaunchAppHealthy />} />
            <Route path="/blog/how-to-talk-about-custom-tools-with-your-team" element={<BlogPostHowToTalkAboutToolsWithTeam />} />
            <Route path="/blog/small-tools-big-impact" element={<BlogPostSmallToolsBigImpact />} />
            <Route path="/blog/workflow-too-messy-to-stay-manual" element={<BlogPostWorkflowTooMessy />} />
            <Route path="/blog/what-sales-teams-need-from-prospecting-tool" element={<BlogPostWhatSalesTeamsNeedProspecting />} />
            <Route path="/blog/internal-tools-fail-when-too-broad" element={<BlogPostInternalToolsFailTooBroad />} />
            <Route path="/blog/reduce-admin-without-more-tool-chaos" element={<BlogPostReduceAdminWithoutChaos />} />
            <Route path="/blog/client-facing-tool-customer-side" element={<BlogPostClientFacingToolCustomerSide />} />
            <Route path="/blog/software-should-remove-decisions" element={<BlogPostSoftwareShouldRemoveDecisions />} />
            <Route path="/blog/sales-to-delivery-handover" element={<BlogPostSalesToDeliveryHandover />} />
            <Route path="/blog/too-many-tools-what-to-fix-first" element={<BlogPostTooManyToolsWhatToFixFirst />} />
            <Route path="/blog/automate-repetitive-qualification" element={<BlogPostAutomateQualification />} />
            <Route path="/blog/internal-tools-start-with-one-frustration" element={<BlogPostInternalToolsStartWithFrustration />} />
            <Route path="/blog/web-app-vs-mobile-app" element={<BlogPostWebAppVsMobileApp />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      <CookieConsent />
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
